import React from 'react';
import { Box, ButtonBase, Chip, Typography } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { useTranslation } from 'react-i18next';
import {
  ENIQ_TIER_PRO,
  ENIQ_TIER_STANDARD,
  getEniqPricePerUser,
} from '../../../../utils/eniqPricing';

const TIERS = [
  { id: ENIQ_TIER_STANDARD, name: 'Standard' },
  { id: ENIQ_TIER_PRO, name: 'Pro' },
];

/**
 * EniqTierSelector - Compact segmented control for the Eniq AI tier,
 * matching the Business Phone tier selector.
 * `hasPhoneAnchor` picks the anchored (21/34) vs standalone (26/39) prices.
 */
const EniqTierSelector = ({ selected, onChange, region, packageTier, hasPhoneAnchor = true, onCompareClick }) => {
  const { t } = useTranslation();
  const symbol = region === 'eu' ? '€' : '$';

  return (
    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Box
        sx={{
          display: 'inline-flex',
          bgcolor: '#f3f4f6',
          borderRadius: 1,
          p: 0.375,
          gap: 0.375,
        }}
      >
        {TIERS.map((tier) => {
          const isSelected = selected === tier.id;
          const price = getEniqPricePerUser(tier.id, packageTier, region, hasPhoneAnchor);

          return (
            <ButtonBase
              key={tier.id}
              onClick={() => onChange(tier.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                py: 0.75,
                px: 2,
                minWidth: 70,
                borderRadius: 0.75,
                transition: 'all 0.2s ease',
                bgcolor: isSelected ? '#fff' : 'transparent',
                boxShadow: isSelected ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                '&:hover': {
                  bgcolor: isSelected ? '#fff' : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: isSelected ? 600 : 500,
                  fontSize: '0.875rem',
                  color: isSelected ? '#00C853' : '#666',
                }}
              >
                {tier.name}
              </Typography>

              {price === 0 ? (
                <Chip
                  label={t('calculator.included', 'INCLUDED')}
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    bgcolor: 'rgba(0, 200, 83, 0.12)',
                    color: '#00C853',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: isSelected ? '#00C853' : '#999',
                  }}
                >
                  +{symbol}{price}
                </Typography>
              )}
            </ButtonBase>
          );
        })}
      </Box>

      {onCompareClick && (
        <ButtonBase
          onClick={onCompareClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: '#666',
            '&:hover': { color: '#333' },
          }}
        >
          <CompareArrowsIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: '0.875rem' }}>
            {t('calculator.compareFeatures', 'Compare features')}
          </Typography>
        </ButtonBase>
      )}
    </Box>
  );
};

export default EniqTierSelector;
